import React from 'react';

import { ProjectDetailsFieldValue } from 'nekst-api';
import { DateField, SimpleBlock, TextLink, useShowContextRequired } from 'ui-builder';
import PriceField from '../../../shared/uibuilder/show/field/PriceField';
import { TextBadge, TextBadgeVariant } from 'features/nekst-widgets';
import { ParseDataFieldWidget } from 'features/tasks-forms';
import useTasksNavigator from '../../../projects/view/tasks/tasksNavigator';
import { ProjectDetailFieldRow } from './types';

function IsCommissionFieldBadge() {
  const showContext = useShowContextRequired<ProjectDetailFieldRow>();

  if (showContext.data.setting.isCommissionField) {
    return (
      <TextBadge
        text="Commission"
        variant={TextBadgeVariant.PRIMARY}
        title="Used to track commission amounts and calculate total commission statistics"
      />
    );
  } else {
    return null;
  }
}

function IsClosingDateFieldBadge() {
  const showContext = useShowContextRequired<ProjectDetailFieldRow>();

  if (showContext.data.setting.isClosingDateField) {
    return (
      <TextBadge
        text="Closing Date"
        variant={TextBadgeVariant.PRIMARY}
        title="Used to track when transactions close and calculate commission statistics by time period"
      />
    );
  } else {
    return null;
  }
}

export function DetailFieldName() {
  const showContext = useShowContextRequired<ProjectDetailFieldRow>();
  const data = showContext.data;

  return (
    <>
      {data.setting.name}
      <ParseDataFieldWidget source={`detailsFields.${data.setting.id}`} />
      {(data.setting.isCommissionField || data.setting.isClosingDateField) && (
        <SimpleBlock>
          <IsCommissionFieldBadge />
          <IsClosingDateFieldBadge />
        </SimpleBlock>
      )}
    </>
  );
}

export function DateFieldValue() {
  const showContext = useShowContextRequired<ProjectDetailFieldRow>();
  const data = showContext.data;

  return (
    <DateField
      source="value"
      prefix={data.approximate ? 'approx.' : undefined}
      noValueText={data.linkedTask ? 'No Due Date' : '-'}
    />
  );
}

export function LinkedTaskCell() {
  const showContext = useShowContextRequired<ProjectDetailFieldRow>();
  const data = showContext.data;
  const tasksNavigator = useTasksNavigator();

  if (data.linkedTask) {
    return (
      <TextLink
        onClick={() => tasksNavigator.openTask(data.linkedTask!.id)}
        href={tasksNavigator.getOpenTaskUri(data.linkedTask!.id)}
      >
        {data.linkedTask.name}
      </TextLink>
    );
  } else {
    return <>-</>;
  }
}

export function PriceFieldValue() {
  const showContext = useShowContextRequired<ProjectDetailsFieldValue>();

  if (showContext.data.value) {
    return <PriceField source="value" />;
  } else {
    return <>-</>;
  }
}

export function TextFieldValue() {
  const showContext = useShowContextRequired<ProjectDetailsFieldValue>();
  const value = (showContext.data.value as string) || '-';

  return value;
}
